import { BadgeProps } from '@chakra-ui/react';

import { date as commonDate } from '@finley-technologies/common';

import { NotificationSubTypeGQLType } from 'types/dashboard-admin-gqlTypes.generated';
import {
  AccountTypeGQLType,
  BorrowerAccountTypeGQLType,
  CapitalProviderAccountTypeGQLType,
  DeliverableStatusKeyGQLType,
  FundingRequestComponentTypeEnumGQLType,
  ReportTypeGQLType,
} from 'types/dashboard-core-gqlTypes.generated';
import { ErrorToastConfig, SuccessToastConfig } from 'types/dashboard.d';
import { PlaidContainerState } from 'types/plaid';
import { TellerSettingsView } from 'types/teller';

import { colors as themeColors } from 'packages/common-frontend/colors';
import { FiLayers } from 'react-icons/fi';

// const lowercaseRegex = /(?=.*[a-z])/;
// const uppercaseRegex = /(?=.*[A-Z])/;
export const numericRegex = /(?=.*[0-9])/;

export const errorToastId = 'errorToastId';
export const errorToastConfig: ErrorToastConfig = {
  id: errorToastId,
  duration: 6000,
  status: 'error',
  isClosable: true,
  position: 'top',
};

export const errorToastAdminConfig: ErrorToastConfig = {
  id: errorToastId,
  status: 'error',
  isClosable: true,
  duration: null,
  position: 'top',
};

export const warnToastId = 'warnToastId';
export const warnToastConfig: ErrorToastConfig = {
  id: warnToastId,
  duration: 6000,
  status: 'warning',
  isClosable: true,
  position: 'top',
};

export const toastId = 'toastId';
export const successToastConfig: SuccessToastConfig = {
  id: toastId,
  duration: 6000,
  status: 'success',
  isClosable: true,
  position: 'top',
};

export const dashboardRoutePadding = '40px';

export const fundingRequestKeyToLabel: {
  [key in FundingRequestComponentTypeEnumGQLType]: string;
} = {
  collectionsPaydown: 'Collections paydown',
  collectionsRecycle: 'Collections recycle',
  curePaydown: 'Cure paydown',
  drawRequest: 'Credit facility draw',
};

export const fundingRequestKeyToVerb: {
  [key in FundingRequestComponentTypeEnumGQLType]: string;
} = {
  collectionsPaydown: 'collections paydown',
  collectionsRecycle: 'collections recycle',
  curePaydown: 'cure paydown',
  drawRequest: 'credit facility draw',
};

// inputs for non chakra ui components must use imported color object reference. Chakra UI components can parse string reperesntations of the color object as ween with fundingRequest.sliderGreen

export const warningRed = themeColors.warning;

export const colors = {
  fundingRequest: {
    sliderGreen: 'green.200', // input for chakra ui component
  },
  insightsCharts: {
    chartFillRed: themeColors.red[200],
    chartStrokeRed: themeColors.red[300],
    chartFillBlue: themeColors.blue[200],
    chartStrokeBlue: themeColors.blue[300],
  },
};

/**
 * Insights consts
 */

export const insightKeyToConfig = {
  borrowingBaseAmountAssets: {
    label: 'Borrowing base from receivable assets',
    icon: FiLayers,
  },
  totalReceivableExposure: {
    label: 'Total exposure',
    icon: FiLayers,
  },
  totalEligibleExposure: {
    label: 'Total eligible exposure',
    icon: FiLayers,
  },
  totalIneligibleExposure: {
    label: 'Total ineligible exposure',
    icon: FiLayers,
  },
  totalExcessConcentration: {
    label: 'Total excess concentration',
    icon: FiLayers,
  },
  totalBorrowingBaseAmount: {
    label: 'Borrowing base',
    icon: FiLayers,
  },
  totalMaxAdvanceAmount: {
    label: 'Max advance amount',
    icon: FiLayers,
  },
  totalAdvanceRate: {
    label: 'Advance rate',
    icon: FiLayers,
  },
  totalFacilityUtilization: {
    label: 'Facility utilization',
    icon: FiLayers,
  },
  concentrationLimitsCount: {
    label: 'Concentration limits',
    icon: FiLayers,
  },
  concentrationLimitsBreachedCount: {
    label: 'Limits breached',
    icon: FiLayers,
  },
  totalCash: {
    label: 'Total Cash Balance',
    icon: FiLayers,
  },
  effectiveAdvanceRate: {
    label: 'Effective Advance Rate',
    icon: FiLayers,
  },
};

export const defaultPlaidInstitutionLogo =
  'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAJgAAACYCAMAAAAvHNATAAAABGdBTUEAALGPC/xhBQAAAAFzUkdCAK7OHOkAAAAJcEhZcwAACxMAAAsTAQCanBgAAAA8UExURUdwTO3t7e7u7uzs7O3t7e3t7e3t7e/v7+zs7O7u7u3t7REREZubm0hISH9/fy8vL9nZ2bq6uh8fH2dnZxAlB0cAAAAKdFJOUwB4X5DFoe8ZQd81kq9CAAADm0lEQVR42u2c2a7qMAxFSZuZAi38/79eaJmHUjtOstE9+wmJSizZjmNSx6tVopxvWqN1CHFUCFobpbxfVZRXRtv4SUc+XwVqhukqq4vCOd8ugLoomEJsJKqz4fKzOUWmOtutcTmNZZhYo0wuNK9jorSHxBrRpK3mRLDEHepUFJQSQ/Mhiio0MuYyUVwS/vQ2ZlCy0VwbM6lNMpoLMZtCAlljY0ZZtjtVzCwFsxpfVmfVXC+7ReUM+5QlUIqLSlaOi0ZWkotCVpaLQKZjYS1cmyYWl4HI98w9oIlV9HXfdLYOmHVYC3Lx0mxjNbWAATbJIzryS5iZWFUG0pEzzqzryJmVqWJ1vd0AXASQw4v8c50BarB38a8xwF5M5mPENJlGAdOQEfZqMoMDZkAN9riXN0BgD+lfZJfsDptOxGSyuaLbrY/adaLhb4SwhNBuRXbqP6Nhs75TMto1/L0klgSal/DkDWu/lXJom74m77F6sVizqZ58xjppK4Hmk7LrO6yjegE0lVBYfMB6QtsMCSWGlcWSQLPMEPuCJYDmOf/aFmAlozX0LNYvw5rQrin30NOLMmLsH5ZijWiXp9dbevQTjbxZjnWPtiFHPzX2O6oBJn9SM5qjL8rRZPvFMdPvxxijRz95UU6/tFv6+FQ/9gXA4jBtN5tF2rEceSowGDXPYU3Ugf4bhrNT9jsa147+E8d8wdnChz2Fa9+zwFhVIsVmvAIjMMvX7j6kxwRy+eY+m3a8wJ/AIiZY/AP7A/t/wWDTBSyYxgTTuGAGE8wwX89nB1PMI5UCYB4TzDPfPRQA450MZwfjHo/lBtPco+HcYIbbPJAbTJEP7oZu1PjX8jB9nk6qz5+76ez19amBGvvE6N+umSKdQ1n64XAZME3vUCkDpugvIMqAOfrLt6FjihL8oX7/3+csBtUM8pAsKraYzicLQF8avMaeR08KvBQXVcDqArypAeiwnsuu2E1HSOHvfqOxDbcVELZ5EsVkboXZb2p+qaUZtgkct22+fvw3v3Y1o/JePnczyUM6EvnCFO4VM9hLebjXGHEvflbJZi3QZf/5IuzHrmPDXmDHvfKPOyQBd6wE7iCOMvmMOVgIdNgL8Hgc3IFCuCOYTu7MYjQrMFQOdcxXBqMFsRl8qKPkRP0JOhcQdJKizjWyM8mheSeJOu4KtSrnEFHuJFHblhoJawKJyq3KCXK07w3O6BmmOsOQb3Reqefx0W2T7r1/3Z/PsFrHmzcAAAAASUVORK5CYII=';

export const plaidContainerInitialState: PlaidContainerState = {
  tab: 'main',
  institutionWithAccounts: {
    itemId: '',
    name: '',
    bankInstitutionKey: 'americanExpress',
    dataProviderInstitutionId: '',
    dataProvider: 'plaid',
    externalItemId: 'id',
    bankInstitutionId: '',
    needsUpdate: false,
    bankAccounts: [],
  },
};

export const tellerContainerInitialState: TellerSettingsView.TellerContainerState = {
  tab: 'main',
  institutionWithAccounts: {
    itemId: '',
    name: '',
    bankInstitutionKey: 'americanExpress',
    dataProviderInstitutionId: '',
    dataProvider: 'teller',
    externalItemId: 'id',
    bankInstitutionId: '',
    needsUpdate: false,
    bankAccounts: [],
  },
};

export const borrowerAccountTypeConfig: {
  [key in BorrowerAccountTypeGQLType]: string;
} = {
  collections: 'Collections account',
  otherReserveAccount: 'Other reserve account',
  parentOperatingAccount: 'Parent operating account',
  preFundingAccount: 'Pre-funding account',
  permittedInvestmentsAccount: 'Permitted investments account',
};

export const capitalProviderAccountTypeConfig: {
  [key in CapitalProviderAccountTypeGQLType]: string;
} = {
  capitalProviderFundingAccount: 'Capital provider funding account',
};

// Empty creditFacilityResponse example
export const emptyCreditFacility = {
  id: '',
  creditFacilitySlug: '',
  isActive: false,
};

export const allDeliverableStatusKeys = [
  'notStarted',
  'upcoming',
  'dueSoon',
  'overdue',
  'completed',
  'waived',
  'expired',
] as const;

export const borrowingBaseContributionAccountTypes: AccountTypeGQLType[] = [
  'collections',
  'preFundingAccount',
  'permittedInvestmentsAccount',
  'accountIncludedInBorrowingBase',
];

export const bankAccountTypes: AccountTypeGQLType[] = [
  'capitalProviderFundingAccount',
  'collections',
  'otherReserveAccount',
  'parentOperatingAccount',
  'preFundingAccount',
  'permittedInvestmentsAccount',
  'accountIncludedInBorrowingBase',
];

export const deliverableStatusLabelDEPRECATED: {
  [status in DeliverableStatusKeyGQLType]: {
    borderColor: string; // indicator border color
    color: string; // indicator fill color
    colorScheme: BadgeProps['colorScheme']; // color for badges
    isSelectOption: boolean;
    label: string;
    value: DeliverableStatusKeyGQLType;
  };
} = {
  notStarted: {
    borderColor: 'blue.300',
    color: 'transparent',
    colorScheme: 'blue',
    isSelectOption: true,
    value: 'notStarted',
    label: 'Not submitted',
  },
  upcoming: {
    borderColor: 'blue.300',
    color: 'transparent',
    colorScheme: 'blue',
    isSelectOption: false,
    value: 'upcoming',
    label: 'Upcoming',
  },
  dueSoon: {
    borderColor: 'orange.300',
    color: 'transparent',
    colorScheme: 'orange',
    isSelectOption: false,
    value: 'dueSoon',
    label: 'Due soon',
  },
  overdue: {
    borderColor: 'red.300',
    color: 'transparent',
    colorScheme: 'red',
    isSelectOption: false,
    value: 'overdue',
    label: 'Overdue',
  },
  expired: {
    borderColor: 'red.300',
    color: 'transparent',
    colorScheme: 'red',
    isSelectOption: false,
    value: 'expired',
    label: 'Expired',
  },
  waived: {
    borderColor: 'gray.300',
    color: 'gray.300',
    colorScheme: 'gray',
    isSelectOption: true,
    value: 'waived',
    label: 'Waived',
  },
  completed: {
    borderColor: 'green.300',
    color: 'green.300',
    colorScheme: 'green',
    isSelectOption: true,
    value: 'completed',
    label: 'Completed',
  },
};

export const reportTypeAcceptedFileTypes: Record<ReportTypeGQLType, any> = {
  cashBalancesFeed: { 'text/*': ['.csv'] },
  monthlyServicerReport: { 'application/*': ['.xlsx', '.xls'] },
  noticeOfBorrowing: { 'application/*': ['.docx', '.doc', '.pdf'] },
  noticeOfPrepayment: { 'application/*': ['.docx', '.doc', '.pdf'] },
  releaseNotice: { 'application/*': ['.docx', '.doc', '.pdf'] },
  responseTape: { 'text/*': ['.csv'] },
  extendedResponseTape: { 'text/*': ['.csv'] },
  newlyAssignedExtendedResponseTape: { 'text/*': ['.csv'] },
  fundingRequestServicerReport: { 'application/*': ['.xlsx', '.xls'] },
  servicerReport: {
    'application/*': ['.xlsx', '.xls'],
  },
  servicerReportPdf: {
    'application/*': ['.pdf'],
  },
  borrowingBaseCertificate: { 'application/*': ['.docx', '.doc', '.pdf'] },
  borrowerSubmittedServicerReport: {
    'application/*': ['.xlsx', '.xls'],
  },
  placeholderReportType: {
    'text/*': ['.csv'],
    'application/*': ['.docx', '.doc', '.pdf', '.xlsx', '.xls'],
  },
  receivablesSaleAgreement: { 'application/*': ['.docx', '.doc', '.pdf'] },
};

export const notificationSubTypeToDescriptionMap: {
  [key in NotificationSubTypeGQLType]: string;
} = {
  assignmentEmail: 'Assignment proposal email',
  digestEmail: 'Digest email at a regular cadence',
  fundingRequestEmail:
    'Two emails: Automatic funding request & Funding request expired',
  fundingRequestSubmittedBorrowerEmail:
    'Two emails: Facility request submission received & Facility request packet (borrower version). This is used for the borrower-initiated funding request workflow.',
  fundingRequestSubmittedCapitalProviderEmail:
    'Facility request packet (capital provider direct version). Note: This option must be toggled on for both the capital provider and borrower(s) for whom you want to receive this email. This is used for the borrower-initiated funding request workflow.',
  upcomingDeliverableEmail: 'Single email per upcoming deliverable',
  servicerReportPreviewEmail: 'Servicer report preview email',
  dataTapeIngestionErrors: 'Data tape ingestion errors email',
  borrowingBaseReportGenerated:
    'Borrowing base report generated. May include servicer report.',
  fundingRequestApprovedWebhook: 'Funding request approved webhook',
  borrowingBaseReportGeneratedWebhook: 'Borrowing base generated webhook',
  assignmentAssignEventWebhook: 'Assignment `assign` event webhook',
  deliverablesResponsiblePartyReminder:
    'Deliverables responsible party reminder email',
  deliverablesResponsiblePartySubmitSubmission:
    'Deliverables responsible party submission email',
  deliverablesResponsiblePartyStatusUpdate:
    'Deliverable status update notification',
  deliverableStatusUpdate:
    'Deliverable status update notification v1 DEPRECATED', // @jendevelops deliverablesV1 deprecated
  fundingRequestApproval: 'Funding request approval notification',
  fundingRequestExpired: 'Funding request expired notification',
  fundingRequestAutoGenerated: 'Funding request was autogenerated notification',
  fundingRequestPostSubmit: 'Funding request submitted notification',
  metricAlert: 'Alert based on metric and thresholds',
  remediationRequestedBorrowerAction:
    'Funding request verification remediation requested notification for borrower',
  remediationRequestedCapitalProviderReceipt:
    'Funding request verification remediation requested receipt notification for capital provider',
  verificationReportAdjudicationAction:
    'Funding request approved or rejected notification for the calc verification workflow.',
  verificationReportCreatedBorrower:
    'Verification report has been generated. Notification for borrower.',
  verificationReportReadyForReviewBorrower:
    'Verification report is ready for review. Receipt notification for borrower.',
  verificationReportReadyForReviewCapitalProvider:
    'Verification report is ready for review. Notification to capital providers.',
};

// this is the number of days before current day that various dashboard areas (eg. portfolio insights) should default to
// 3 years default load until we have global filters
export const defaultDashboardDisplayDays: number = 365 * 3;
export const defaultDashboardDisplayMonths: number = 36;
// init conditions: 3 years from current
export const getDefaultDashboardDisplayEndDate = () =>
  commonDate.currentDateStr();
export const getDefaultDashboardDisplayStartDate = () =>
  commonDate.addDays(
    getDefaultDashboardDisplayEndDate(),
    -defaultDashboardDisplayDays,
  );
